import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { FaArrowRight } from 'react-icons/fa';
import { defaultViewport, getRevealProps, staggerContainer, staggerItem } from '../motion/scrollMotion';
import ScrollReveal from './ScrollReveal';
import PentestMockup from './PentestMockup';
import './SocOptimizationSection.css';

const SocOptimizationSection = () => {
  return (
    <ScrollReveal as="section" className="soc-opt-section" variant="fadeUp">
      <div className="soc-opt-container">
        {/* Header Section */}
        <div className="soc-opt-header">
          <motion.h2 className="soc-opt-main-title" {...getRevealProps('fadeUp')}>
            Featured Services<br />Across Every Discipline.
          </motion.h2>
          <motion.p className="soc-opt-subtitle" {...getRevealProps('fadeUp', 0.12)}>
            Proven InfoSec delivers integrated cybersecurity, managed IT, and secure software
            development under one roof. From expert-led penetration testing and multi-cloud
            security to 24/7 SIEM monitoring, endpoint management, and AI-powered solutions —
            we close the gaps that fragmented vendors leave behind.
          </motion.p>
        </div>

        {/* Bento Grid */}
        <motion.div
          className="soc-opt-grid"
          initial="hidden"
          whileInView="visible"
          viewport={defaultViewport}
          variants={{
            ...staggerContainer,
            visible: { transition: { staggerChildren: 0.15, delayChildren: 0.08 } },
          }}
        >
          
          {/* Card 1: Agentic Triage */}
          <motion.div 
            className="soc-opt-card"
            variants={staggerItem}
            whileHover={{ scale: 1.01, backgroundColor: "#15141f" }}
          >
            <div className="card-ui-header ui-indigo">
              <span className="ui-stat">CISSP · CEH · OSCP</span>
              <span className="ui-label">certified penetration testers on every engagement</span>
            </div>

            <div className="card-mockup-container">
              <PentestMockup />
            </div>

            <div className="card-content">
              <span className="card-badge">Penetration Testing</span>
              <h3 className="card-title">Identify Vulnerabilities Before Attackers Do.</h3>
              <p className="card-desc">
                Expert-led penetration testing across network, application, cloud, and wireless
                environments — using the same techniques as real-world threat actors. Validate
                security controls, meet ISO 27001 / NIST CSF / CIS Controls requirements, and reduce breach risk.
              </p>
              <Link to="/services/vapt" className="card-btn">
                Explore Penetration Testing <FaArrowRight />
              </Link>
            </div>
          </motion.div>

          {/* Card 2: Detection-as-Code */}
          <motion.div 
            className="soc-opt-card"
            variants={staggerItem}
            whileHover={{ scale: 1.01, backgroundColor: "#15141f" }}
          >
            <div className="card-ui-header ui-cyan">
              <span className="ui-stat">AWS · Azure · GCP</span>
              <span className="ui-label">multi-cloud security assessments & architecture hardening</span>
            </div>

            <div className="card-mockup-container">
              <img src="/socops2-cloud.svg" alt="Cloud Security" className="card-mockup-img" />
            </div>

            <div className="card-content">
              <span className="card-badge">Cloud Security</span>
              <h3 className="card-title">Secure Every Cloud You Operate In.</h3>
              <p className="card-desc">
                Configuration reviews and architecture hardening for AWS, Azure, and GCP — covering
                IAM, storage, serverless, containers (Docker, Kubernetes), and infrastructure-as-code.
                Most cloud breaches come from misconfigurations. We find them before attackers do.
              </p>
              <Link to="/services/cloud-security-audit" className="card-btn">
                Explore Cloud Security <FaArrowRight />
              </Link>
            </div>
          </motion.div>

          {/* Card 3: Tuning Agents */}
          <motion.div 
            className="soc-opt-card"
            variants={staggerItem}
            whileHover={{ scale: 1.01, backgroundColor: "#15141f" }}
          >
            <div className="card-ui-header ui-magenta">
              <span className="ui-stat">24/7</span>
              <span className="ui-label">SOC monitoring with expert analyst-led triage</span>
            </div>

            <div className="card-mockup-container">
              <img src="/socops3-monitoring.svg" alt="Security Monitoring" className="card-mockup-img" />
            </div>

            <div className="card-content">
              <span className="card-badge">Security Monitoring</span>
              <h3 className="card-title">24/7 SIEM Monitoring & Incident Response.</h3>
              <p className="card-desc">
                Continuous SIEM monitoring, threat detection, and coordinated incident response
                from our 24/7 SOC. Enterprise-grade detection tuning, false-positive reduction,
                and analyst-led investigation — fully managed or co-managed alongside your team.
              </p>
              <Link to="/services/security-operations-centre" className="card-btn">
                Explore Security Monitoring <FaArrowRight />
              </Link>
            </div>
          </motion.div>

          {/* Card 4: Cost Savings */}
          <motion.div 
            className="soc-opt-card"
            variants={staggerItem}
            whileHover={{ scale: 1.01, backgroundColor: "#15141f" }}
          >
            <div className="card-ui-header ui-teal">
              <span className="ui-stat">UEM</span>
              <span className="ui-label">unified endpoint management, protection & policy enforcement</span>
            </div>

            <div className="card-mockup-container">
              <img src="/socops4-managedit.svg" alt="Managed IT" className="card-mockup-img" />
            </div>

            <div className="card-content">
              <span className="card-badge">Managed IT</span>
              <h3 className="card-title">Endpoint Management & Backup-Ready IT.</h3>
              <p className="card-desc">
                Centralized endpoint management with EDR, automated patch management, identity &
                access governance, and ransomware-resilient backup with tested disaster recovery.
                IT operations run with cybersecurity principles embedded into every process.
              </p>
              <Link to="/managed-it" className="card-btn">
                Explore Managed IT <FaArrowRight />
              </Link>
            </div>
          </motion.div>

        </motion.div>
      </div>
    </ScrollReveal>
  );
};

export default SocOptimizationSection;
